import { useState, useEffect, useRef, useCallback } from 'react'
import { transcriptSummaryService } from '../services/transcriptSummaryService'
import { propertiesService } from '../../../services/propertiesService'
import { useAuthStore } from '../../auth/store/authStore'
import type { TranscriptSummary } from '../../../types/transcript.types'

export interface PropertyOption {
  id: number
  name: string
}

export function useTranscriptSummary(day: string, selectedPropertyId?: number): {
  summary: TranscriptSummary | null
  propertyName: string | null
  loading: boolean
  regenerating: boolean
  error: string | null
  regenerate: () => void
  canRegenerate: boolean
  needsPropertySelection: boolean
  propertyOptions: PropertyOption[]
} {
  const user = useAuthStore((s) => s.user)
  // No fallback to the user's first property — see useTranscripts. Which also
  // means "nothing selected" is now the only way to need a selection, rather
  // than the SUPER_ADMIN-with-no-properties corner it used to be.
  const propertyId = selectedPropertyId ?? undefined
  const needsPropertySelection = propertyId === undefined
  const canRegenerate = user?.role === 'MANAGER' || user?.role === 'SUPER_ADMIN'

  const [summary, setSummary] = useState<TranscriptSummary | null>(null)
  const [propertyName, setPropertyName] = useState<string | null>(null)
  const [propertyOptions, setPropertyOptions] = useState<PropertyOption[]>([])
  const [loading, setLoading] = useState(false)
  const [regenerating, setRegenerating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const callIdRef = useRef(0)

  const fetchSummary = useCallback(
    async (refresh: boolean) => {
      if (propertyId === undefined) return
      const callId = ++callIdRef.current
      if (refresh) setRegenerating(true)
      else setLoading(true)
      setError(null)
      try {
        const data = await transcriptSummaryService.getDailySummary({ propertyId, day, refresh })
        if (callId !== callIdRef.current) return
        if (data) setSummary(data)
        else setError('Failed to load AI summary.')
      } catch (err) {
        if (callId !== callIdRef.current) return
        setError(err instanceof Error ? err.message : 'Failed to load AI summary.')
      } finally {
        if (callId === callIdRef.current) {
          setLoading(false)
          setRegenerating(false)
        }
      }
    },
    [propertyId, day],
  )

  useEffect(() => { fetchSummary(false) }, [fetchSummary])

  useEffect(() => {
    if (propertyId === undefined) { setPropertyName(null); return }
    let cancelled = false
    propertiesService.getById(propertyId).then((prop) => {
      if (cancelled) return
      setPropertyName(prop?.name ?? null)
    })
    return () => { cancelled = true }
  }, [propertyId])

  useEffect(() => {
    if (!needsPropertySelection) return
    let cancelled = false
    propertiesService.getAll().then((props) => {
      if (cancelled) return
      setPropertyOptions(props.map((p) => ({ id: p.id, name: p.name })))
    })
    return () => { cancelled = true }
  }, [needsPropertySelection])

  const regenerate = useCallback(() => { fetchSummary(true) }, [fetchSummary])

  return {
    summary, propertyName, loading, regenerating, error,
    regenerate, canRegenerate, needsPropertySelection, propertyOptions,
  }
}
